import React, { useEffect, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import backendClient from '@services/api/backendClient';
import {
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

export type UrlsGatheredData = string[];

export type HtmlParsedData = {
  title: string;
  description: string;
};

export type DataExtracted = {
  content: string;
  metaData: string;
};

export type CrawlJobProgress<T> = {
  rootJobId: string;
  jobId: string;
  name: string;
  status: CrawlJobStatus;
  progress: number;
  result?: T;
};

export enum CrawlJobStatus {
  Pending = 'pending',
  InProgress = 'in-progress',
  Removed = 'removed',
  Added = 'added',
  Completed = 'completed',
  Stopped = 'stopped',
  Failed = 'failed',
}

export type CrawlJobData = DataExtracted | HtmlParsedData | UrlsGatheredData;
export interface CrawlJob {
  rootId: string;
  id: string;
  name: string;
  status: string;
  progress: number;
  data: CrawlJobData;
  attempts: number;
  failedReason?: string;
}

const SOCKET_IO_URL = 'http://localhost:8082';
const CrawlQueueTable: React.FC = () => {
  const [jobNodes, setJobNodes] = useState<Map<string, CrawlJob[]>>(new Map());
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    const newSocket = io(SOCKET_IO_URL, {
      path: '/socket.io/',
      transports: ['websocket'],
    });
    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await backendClient.get<CrawlJob[]>('/jobs');
      console.log('Jobs fetched:', response.data);
      const jobs = response.data.reduce((acc, job) => {
        if (!acc.has(job.rootId)) {
          acc.set(job.rootId, []);
        }
        acc.get(job.rootId)?.push(job);
        return acc;
      }, new Map<string, CrawlJob[]>());
      setJobNodes(jobs);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  };

  const handleRerun = async (jobId: string) => {
    await backendClient.post(`/jobs/${jobId}/rerun`);
  };

  const handleStop = async (jobId: string) => {
    await backendClient.post(`/jobs/${jobId}/stop`);
  };

  const handleJobUpdate = (update: CrawlJobProgress<CrawlJobData>) => {
    console.log('----------');
    console.log('Job update:', update);
    setJobNodes((prevJobs) => {
      const rootId = update.rootJobId;
      const newJobs = new Map(prevJobs);
      if (!newJobs.has(rootId)) {
        newJobs.set(rootId, []);
      }
      const rootJobs = newJobs.get(rootId);
      const notRootJob = update.status !== CrawlJobStatus.Added;
      if (!notRootJob) {
        console.log('Root job:', update.jobId);
        console.log('Status:', update.status);
        console.log('Progress:', update.progress);
        console.log('Data:', update.result);
        console.log('----------');
        newJobs.set(rootId, []);
        return newJobs;
      }

      if (rootJobs !== undefined) {
        console.log('Updating job:', update.jobId);
        console.log('With root:', update.rootJobId);
        const jobIndex = rootJobs.findIndex((job) => job.id === update.jobId);
        const jobExists = jobIndex !== -1;
        console.log('Job exists:', jobExists);
        if (jobExists) {
          rootJobs[jobIndex].status = update.status;
          rootJobs[jobIndex].progress = update.progress;
          if (update.status === CrawlJobStatus.Completed) {
            rootJobs[jobIndex].data = update.result as unknown as CrawlJobData;
          }
        } else {
          console.log('Adding new job:', update.jobId);
          //add new job
          rootJobs.push({
            id: update.jobId,
            rootId: update.rootJobId,
            name: update.name,
            status: update.status,
            progress: update.progress,
            data: update.result as unknown as CrawlJobData,
            attempts: 0,
          });
        }
      } else {
        console.log('Adding new job:', update.jobId);
        //add new job
        newJobs.set(rootId, [
          {
            id: update.jobId,
            rootId: update.rootJobId,
            name: update.name,
            status: update.status,
            progress: update.progress,
            data: update.result as unknown as CrawlJobData,
            attempts: 0,
          },
        ]);
      }
      console.log('----------');

      return newJobs;
    });
  };

  useEffect(() => {
    fetchJobs();
    if (socket) {
      socket.on('job_update', handleJobUpdate);
    }

    return () => {
      socket?.off('job_update', handleJobUpdate);
    };
  }, [socket]);

  const addJob = async () => {
    try {
      const jobData = { description: 'New Job' }; // Ensure this matches your JobData interface
      await backendClient.post('/jobs/add', jobData);
    } catch (error) {
      console.error('Failed to add job:', error);
    }
  };

  return (
    <TableContainer sx={{ marginTop: '1.5rem' }} component={Paper}>
      <Table aria-label="jobs table">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Progress</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from(jobNodes).map(([rootId, rootJobs]) => (
            <TableRow key={rootId}>
              <TableCell>{rootId}</TableCell>
              <TableCell>
                {rootJobs.map((job) => (
                  <div key={job.id}>
                    {job.name} ({job.progress}% - {job.status})
                    {job.status === 'InProgress' && (
                      <CircularProgress
                        variant="determinate"
                        value={job.progress}
                      />
                    )}
                  </div>
                ))}
              </TableCell>
              <TableCell>
                {rootJobs
                  .reduce((prev, curr) => `${prev}, ${curr.status}`, '')
                  .slice(2)}
              </TableCell>
              <TableCell>
                <Button onClick={() => handleRerun(rootId)}>Rerun</Button>
                <Button onClick={() => handleStop(rootId)}>Stop</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button
        variant="contained"
        color="primary"
        onClick={addJob}
        style={{ margin: 16 }}
      >
        Add Job
      </Button>
    </TableContainer>
  );
};

export default CrawlQueueTable;
